import { useState, useEffect } from 'react';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex overflow-hidden">

      {/* ─── LEFT — content panel ─── */}
      <div
        className={`relative z-20 w-full lg:w-[50%] bg-[#F9F8F5] flex flex-col justify-center px-8 md:px-14 lg:px-20 pt-32 lg:pt-24 pb-16 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        {/* Overline */}
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-px bg-[#C4A96E]" />
          <span
            className="text-[#C4A96E] text-[10px] tracking-[0.36em] uppercase font-semibold"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Orthodontics · Chatsworth, CA
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-[54px] md:text-[68px] lg:text-[76px] text-[#1A2118] font-semibold leading-[1.0] mb-7"
          style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '-0.02em' }}
        >
          The Smile<br />
          <em
            className="not-italic font-light"
            style={{ color: '#4C8265' }}
          >
            You Deserve
          </em><br />
          <span className="font-light text-[#1A2118]/70">Starts Here.</span>
        </h1>

        {/* Subtext */}
        <p
          className="text-[#5A7060] text-base md:text-lg leading-[1.85] mb-10 max-w-[430px]"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Personalized orthodontic care for children, teens, and adults — where advanced technology meets a truly patient-first experience.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3.5 mb-12">
          <a
            href="https://smilebydrkortez.com/request-appointment"
            target="_blank"
            rel="noopener nofollow"
            className="bg-[#1C3528] hover:bg-[#254535] text-white font-semibold text-sm px-9 py-4 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 flex items-center justify-center gap-2.5"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Book Appointment
            <i className="ri-arrow-right-line text-base" />
          </a>
          <a
            href="https://smilebydrkortez.com/virtual-consult"
            target="_blank"
            rel="noopener nofollow"
            className="border border-[#1C3528]/25 hover:border-[#1C3528]/60 hover:bg-[#1C3528]/5 text-[#1C3528] font-medium text-sm px-9 py-4 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 flex items-center justify-center gap-2.5"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            <i className="ri-video-line" />
            Virtual Consultation
          </a>
        </div>

        {/* Trust pillars */}
        <div className="flex flex-col gap-3">
          {[
            { icon: 'ri-user-heart-line', label: 'Personalized Treatment Plans' },
            { icon: 'ri-microscope-line', label: 'Advanced Orthodontic Technology' },
            { icon: 'ri-team-line', label: 'Children, Teens & Adults Welcome' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#EBF5EF] rounded-lg flex-shrink-0">
                <i className={`${item.icon} text-[#4C8265] text-xs`} />
              </div>
              <span
                className="text-[#5A7060] text-sm"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Floating address card — bottom of panel */}
        <div className="hidden lg:flex items-center gap-6 mt-12 pt-10 border-t border-[#1A2118]/10">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-[#C4A96E] text-sm" />
            </div>
            <span
              className="text-[#5A7060] text-xs"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              10234 Canoga Ave, Chatsworth, CA 91311
            </span>
          </div>
          <span className="w-px h-4 bg-[#1A2118]/15" />
          <a
            href="tel:8183415150"
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-phone-line text-[#C4A96E] text-sm" />
            </div>
            <span
              className="text-[#5A7060] text-xs group-hover:text-[#1C3528] transition-colors"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              (818) 341-5150
            </span>
          </a>
        </div>
      </div>

      {/* ─── RIGHT — editorial image ─── */}
      <div className="hidden lg:block lg:w-[50%] relative">
        <img
          src="https://public.readdy.ai/ai/img_res/dc916ba51c7fb4a5c904508cf50e202f.jpg"
          alt="Confident orthodontic smile — Smile By Dr. K Chatsworth CA"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* Left-edge fade to white panel */}
        <div className="absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#F9F8F5] to-transparent" />
        {/* Bottom fade */}
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#F9F8F5]/40 to-transparent" />

        {/* Floating "Now Accepting" badge */}
        <div className="absolute top-1/2 right-10 -translate-y-1/2 bg-white/90 backdrop-blur-md rounded-2xl px-5 py-4 border border-white/80">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-[#4C8265] animate-pulse" />
            <span
              className="text-[#4C8265] text-[9px] tracking-[0.22em] uppercase font-bold"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Now Accepting
            </span>
          </div>
          <p
            className="text-[#1A2118] font-semibold text-sm leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '17px' }}
          >
            New Patients
          </p>
          <p
            className="text-[#5A7060] text-[10px] mt-1"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            All ages welcome
          </p>
        </div>
      </div>

      {/* Mobile background image (sm only) */}
      <div className="absolute inset-0 lg:hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stunning%20confident%20woman%20with%20radiant%20perfect%20smile%20and%20bright%20white%20teeth%2C%20warm%20golden%20hour%20lighting%2C%20soft%20bokeh%20background%20with%20creamy%20ivory%20warm%20tones%2C%20ultra%20high-end%20editorial%20portrait%20photography%2C%20luxury%20beauty%20healthcare%20branding%20aesthetic%2C%20flawless%20healthy%20glowing%20skin%2C%20sophisticated%20elegant%20mood%2C%20professional%20studio%20lighting%20with%20natural%20warmth%2C%20photorealistic%20premium%20quality%20portrait%2C%20clean%20light%20minimal%20warm%20background&width=960&height=1280&seq=hero2026split01&orientation=portrait"
          alt="Smile By Dr. K Orthodontics"
          className="w-full h-full object-cover object-top opacity-10"
        />
      </div>

    </section>
  );
}
